import { useEffect, useState } from "react";
import { Loader2, Lock, LogOut } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Navbar, Footer } from "../components/SiteChrome";
import {
  fetchBookings,
  formatUSD,
  adminLogin,
  adminVerify,
  setAdminToken,
  clearAdminToken,
  getAdminToken,
} from "../lib/api";

const STATUSES = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "initiated", label: "Initiated" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

const BookingsList = () => {
  const [authed, setAuthed] = useState(null); // null=checking, true, false
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Verify existing token on mount
  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      setAuthed(false);
      return;
    }
    adminVerify()
      .then(() => setAuthed(true))
      .catch(() => {
        clearAdminToken();
        setAuthed(false);
      });
  }, []);

  useEffect(() => {
    if (authed !== true) return;
    setLoading(true);
    fetchBookings(filter === "all" ? null : filter)
      .then(setItems)
      .catch((e) => {
        if (e?.response?.status === 401) {
          clearAdminToken();
          setAuthed(false);
        } else {
          setItems([]);
        }
      })
      .finally(() => setLoading(false));
  }, [filter, authed]);

  const onLogout = () => {
    clearAdminToken();
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <div>
        <Navbar />
        <section className="max-w-3xl mx-auto px-6 py-24 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#0F1A36] mx-auto" />
        </section>
      </div>
    );
  }

  if (authed === false) {
    return <PasswordGate onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div>
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="overline mb-6">Admin · Bookings</div>
            <h1 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-none">
              The book.
            </h1>
            <p className="mt-6 text-[#5c5a56] max-w-xl">
              Every kickoff that&rsquo;s on the calendar — or wanted to be.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 text-sm text-[#5c5a56] hover:text-[#0F1A36] transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        <div className="mt-10">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="bookings-filter-tabs" className="rounded-none bg-transparent border border-[#d4cdb8] p-1">
              {STATUSES.map((s) => (
                <TabsTrigger
                  key={s.value}
                  data-testid={`filter-${s.value}`}
                  value={s.value}
                  className="rounded-none data-[state=active]:bg-[#0F1A36] data-[state=active]:text-[#f5f1e8] font-display"
                >
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-10 border border-[#d4cdb8] bg-white">
          <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#d4cdb8] text-xs uppercase tracking-[0.2em] font-semibold text-[#5c5a56]">
            <div className="col-span-4">Client</div>
            <div className="col-span-3">Offering</div>
            <div className="col-span-2">Kickoff</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {loading && (
            <div className="px-6 py-10 text-sm text-[#5c5a56]">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div data-testid="bookings-empty" className="px-6 py-10 text-sm text-[#5c5a56]">
              No bookings yet.
            </div>
          )}
          {!loading &&
            items.map((b) => (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#d4cdb8] last:border-0 items-center"
              >
                <div className="col-span-4">
                  <div className="font-display text-base font-medium">
                    {b.client.name}
                  </div>
                  <div className="text-xs text-[#5c5a56]">
                    {b.client.email} · {b.client.phone}
                  </div>
                </div>
                <div className="col-span-3">
                  <div className="text-sm">{b.offering_name}</div>
                  <div className="text-xs text-[#5c5a56]">{b.tier_name}</div>
                </div>
                <div className="col-span-2 text-sm">
                  {b.slot_date}
                  <div className="text-xs text-[#5c5a56]">{b.slot_time}</div>
                </div>
                <div className="col-span-1 text-right font-display">
                  {formatUSD(b.amount)}
                </div>
                <div className="col-span-2 text-right">
                  <StatusBadge status={b.payment_status} />
                </div>
              </div>
            ))}
        </div>
      </section>
      <Footer />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    paid: { className: "bg-[#0F1A36] text-[#f5f1e8] border-[#0F1A36]", label: "Paid" },
    initiated: { className: "bg-[#ebe3cf] text-[#0F1A36] border-[#d4cdb8]", label: "Initiated" },
    expired: { className: "bg-white text-[#5c5a56] border-[#d4cdb8]", label: "Expired" },
    cancelled: { className: "bg-white text-[#5c5a56] border-[#d4cdb8]", label: "Cancelled" },
    failed: { className: "bg-red-100 text-red-800 border-red-200", label: "Failed" },
  };
  const conf = map[status] || map.initiated;
  return (
    <Badge
      variant="outline"
      className={`rounded-none font-display ${conf.className}`}
    >
      {conf.label}
    </Badge>
  );
};

const PasswordGate = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { token } = await adminLogin(password);
      setAdminToken(token);
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Navbar />
      <section className="max-w-md mx-auto px-6 py-20 md:py-32">
        <div className="border border-[#d4cdb8] bg-white p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-5 h-5 text-[#C9A961]" />
            <div className="overline">Admin access</div>
          </div>
          <h1 className="font-display font-semibold text-3xl tracking-tight leading-tight">
            Enter password
          </h1>
          <p className="mt-3 text-sm text-[#5c5a56]">
            This page contains customer bookings and contact info.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#5c5a56] mb-2">
                Password
              </span>
              <input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                required
                className="w-full bg-transparent border border-[#d4cdb8] rounded-none px-4 py-3 focus:border-[#0F1A36] focus:outline-none focus:ring-1 focus:ring-[#0F1A36] transition-colors font-body"
              />
            </label>
            {error && (
              <div data-testid="admin-login-error" className="text-xs text-red-700">
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting || !password}
              className="btn-primary w-full justify-center"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                </>
              ) : (
                "Unlock"
              )}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default BookingsList;
